/**
 * Eingebaute Standard-Bibliothek. Läuft auch ohne Admin-Daten; Einträge aus
 * `/editor/preview3d/library` mit gleichem Slug ersetzen diese, neue kommen hinzu.
 */
import type { BodyKind, LibraryFrameProfile, LibraryMaterial, Preview3dBody, Preview3dLibrary } from './types.ts'

const mat = (slug: string, name: string, kind: LibraryMaterial['kind'], over: Partial<LibraryMaterial> = {}): LibraryMaterial => ({
  slug,
  name,
  kind,
  color: '#ffffff',
  roughness: 0.6,
  metalness: 0,
  clearcoat: 0,
  clearcoatRoughness: 0.1,
  transmission: 0,
  ior: 1.5,
  sheen: 0,
  attenuationColor: '#ffffff',
  colorMapUrl: null,
  normalMapUrl: null,
  roughnessMapUrl: null,
  normalScale: 1,
  tileCm: 20,
  procedural: 'none',
  ...over,
})

export const BUILTIN_MATERIALS: LibraryMaterial[] = [
  mat('kiefer-roh', 'Kiefer roh (Keilrahmen)', 'wood', { color: '#d9b98a', roughness: 0.78, procedural: 'wood', tileCm: 30 }),
  mat('eiche-natur', 'Eiche natur', 'wood', { color: '#c49a63', roughness: 0.55, procedural: 'wood', tileCm: 40 }),
  mat('nussbaum', 'Nussbaum', 'wood', { color: '#6b4a32', roughness: 0.5, procedural: 'wood', tileCm: 40 }),
  mat('lack-schwarz', 'Schwarz lackiert', 'paint', { color: '#1b1b1d', roughness: 0.42, clearcoat: 0.25, procedural: 'grain', tileCm: 40 }),
  mat('lack-weiss', 'Weiß lackiert', 'paint', { color: '#f2f1ee', roughness: 0.45, clearcoat: 0.2, procedural: 'grain', tileCm: 40 }),
  mat('leinwand', 'Leinwand (Gewebe)', 'canvas', { color: '#f4f1ea', roughness: 0.92, procedural: 'weave', tileCm: 2, normalScale: 0.6 }),
  mat('alu-gebuerstet', 'Aluminium gebürstet', 'metal', { color: '#cfd1d4', roughness: 0.32, metalness: 0.9, procedural: 'brushed', tileCm: 20 }),
  mat('alu-schwarz', 'Aluminium schwarz eloxiert', 'metal', { color: '#2a2b2e', roughness: 0.38, metalness: 0.8, procedural: 'brushed', tileCm: 20 }),
  mat('acrylglas', 'Acrylglas klar', 'acrylic', { color: '#ffffff', roughness: 0.03, transmission: 1, ior: 1.49, clearcoat: 1, clearcoatRoughness: 0.02, attenuationColor: '#e8f4ef' }),
  mat('glas', 'Glas', 'glass', { color: '#ffffff', roughness: 0.02, transmission: 1, ior: 1.52, attenuationColor: '#eef6f2' }),
  mat('glas-entspiegelt', 'Glas entspiegelt', 'glass', { color: '#ffffff', roughness: 0.12, transmission: 1, ior: 1.52, attenuationColor: '#f2f6f4' }),
  mat('hartschaum-weiss', 'Hartschaum weiß', 'foam', { color: '#f5f5f3', roughness: 0.85, procedural: 'grain', tileCm: 10 }),
  mat('leichtschaum', 'Leichtschaum (Kern)', 'foam', { color: '#f3f1ea', roughness: 0.9, procedural: 'grain', tileCm: 6 }),
  mat('holz-birke', 'Birke Multiplex (Kante)', 'wood', { color: '#e3c99f', roughness: 0.7, procedural: 'wood', tileCm: 25 }),
  mat('passepartout-weiss', 'Passepartout weiß', 'paper', { color: '#f7f5ef', roughness: 0.95, procedural: 'paper', tileCm: 10 }),
  mat('papier-matt', 'Papier matt', 'paper', { color: '#ffffff', roughness: 0.9, procedural: 'paper', tileCm: 10 }),
  mat('rueckwand-hdf', 'Rückwand HDF', 'wood', { color: '#8a6f52', roughness: 0.85, procedural: 'grain', tileCm: 20 }),
]

const profile = (slug: string, name: string, over: Partial<LibraryFrameProfile>): LibraryFrameProfile => ({
  slug,
  name,
  type: 'classic',
  shape: 'flat',
  faceWidthMm: 20,
  depthMm: 25,
  gapMm: 0,
  rebateMm: 5,
  material: 'eiche-natur',
  glassMaterial: '',
  matWidthMm: 0,
  matMaterial: 'passepartout-weiss',
  thumbnailUrl: null,
  ...over,
})

export const BUILTIN_PROFILES: LibraryFrameProfile[] = [
  profile('schattenfuge-natur', 'Schattenfugenrahmen Natur', { type: 'shadow-gap', faceWidthMm: 10, depthMm: 33, gapMm: 8, rebateMm: 0, material: 'eiche-natur' }),
  profile('schattenfuge-schwarz', 'Schattenfugenrahmen Schwarz', { type: 'shadow-gap', faceWidthMm: 10, depthMm: 33, gapMm: 8, rebateMm: 0, material: 'lack-schwarz' }),
  profile('schattenfuge-weiss', 'Schattenfugenrahmen Weiß', { type: 'shadow-gap', faceWidthMm: 10, depthMm: 33, gapMm: 8, rebateMm: 0, material: 'lack-weiss' }),
  profile('rahmen-eiche-flach', 'Holzrahmen Eiche flach', { faceWidthMm: 20, depthMm: 22, material: 'eiche-natur', glassMaterial: 'glas' }),
  profile('rahmen-schwarz-flach', 'Holzrahmen Schwarz flach', { faceWidthMm: 20, depthMm: 22, material: 'lack-schwarz', glassMaterial: 'glas' }),
  profile('rahmen-weiss-flach', 'Holzrahmen Weiß flach', { faceWidthMm: 20, depthMm: 22, material: 'lack-weiss', glassMaterial: 'glas' }),
  profile('alu-rahmen-schwarz', 'Alurahmen Schwarz schmal', { faceWidthMm: 10, depthMm: 20, shape: 'stepped', material: 'alu-schwarz', glassMaterial: 'glas' }),
  profile('posterleiste-eiche', 'Posterleiste Eiche', { type: 'poster-rail', faceWidthMm: 20, depthMm: 10, rebateMm: 10, material: 'eiche-natur' }),
  profile('posterleiste-schwarz', 'Posterleiste Schwarz', { type: 'poster-rail', faceWidthMm: 20, depthMm: 10, rebateMm: 10, material: 'lack-schwarz' }),
]

export const BUILTIN_LIBRARY: Preview3dLibrary = { materials: BUILTIN_MATERIALS, profiles: BUILTIN_PROFILES }

/** Standard-Körper je Art (Werte, die der Admin nicht setzt) */
export function defaultBody(kind: BodyKind = 'canvas'): Preview3dBody {
  const base: Preview3dBody = {
    kind,
    thicknessMm: 20,
    barWidthMm: 40,
    edge: 'mirror',
    edgeColor: '#ffffff',
    surface: 'matt',
    hanging: 'none',
    faceMaterial: '',
    edgeMaterial: '',
    backMaterial: '',
    supportMaterial: '',
  }
  switch (kind) {
    case 'canvas':
      return { ...base, faceMaterial: 'leinwand', backMaterial: 'leinwand', supportMaterial: 'kiefer-roh' }
    case 'plate':
      return { ...base, thicknessMm: 3, edge: 'color', edgeMaterial: 'alu-gebuerstet', backMaterial: 'alu-gebuerstet', hanging: 'rail', supportMaterial: 'alu-gebuerstet' }
    case 'acrylic':
      return { ...base, thicknessMm: 5, edge: 'color', surface: 'gloss', faceMaterial: 'acrylglas', backMaterial: 'alu-gebuerstet', hanging: 'rail', supportMaterial: 'alu-gebuerstet' }
    case 'paper':
      return { ...base, thicknessMm: 0.3, edge: 'white', faceMaterial: 'papier-matt', backMaterial: 'papier-matt' }
  }
}

/** Bibliothek aus API mit eingebauten Einträgen zusammenführen (API gewinnt per Slug) */
export function mergeLibrary(api: Partial<Preview3dLibrary> | null | undefined): Preview3dLibrary {
  const materials = new Map(BUILTIN_MATERIALS.map((m) => [m.slug, m]))
  for (const m of api?.materials ?? []) if (m?.slug) materials.set(m.slug, { ...(materials.get(m.slug) ?? mat(m.slug, m.name || m.slug, m.kind || 'paint')), ...stripNullish(m) })
  const profiles = new Map(BUILTIN_PROFILES.map((p) => [p.slug, p]))
  for (const p of api?.profiles ?? []) if (p?.slug) profiles.set(p.slug, { ...(profiles.get(p.slug) ?? profile(p.slug, p.name || p.slug, {})), ...stripNullish(p) })
  return { materials: [...materials.values()], profiles: [...profiles.values()] }
}

function stripNullish<T extends object>(o: T): Partial<T> {
  const out: Partial<T> = {}
  for (const [k, v] of Object.entries(o)) {
    // Textur-URLs dürfen null sein (= keine Datei), alles andere null/undefined = Standard behalten
    if (v === undefined) continue
    if (v === null && !/Url$/.test(k)) continue
    ;(out as any)[k] = v
  }
  return out
}
